import { useState, useEffect, useCallback } from 'react';
import { 
  fetchBlogPosts, 
  addBlogPost, 
  updateBlogPost, 
  deleteBlogPost, 
  bulkDeleteBlogPosts 
} from '../../services/AdminBlogService';
import { fetchBlogCategories } from '../../services/AdminBlogService';

export const useBlogPostActions = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: '',
  });

  // Fetch posts and categories
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [postsData, categoriesData] = await Promise.all([
        fetchBlogPosts(),
        fetchBlogCategories()
      ]);
      setPosts(postsData);
      setCategories(categoriesData);
      setError(null);
    } catch (err) {
      setError('Error fetching data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter posts based on current filters
  const filteredPosts = posts.filter(post => {
    const matchesSearch = !filters.search || 
      post.title.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesCategory = !filters.category || 
      (post.category && post.category.id.toString() === filters.category);
    
    const matchesStatus = !filters.status || 
      post.status === filters.status;

    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Add new post
  const handleAddPost = useCallback(async (formData) => {
    try {
      const newPost = await addBlogPost(formData);
      setPosts(prevPosts => [newPost, ...prevPosts]);
      return newPost;
    } catch (err) {
      setError('Error adding post');
      throw err;
    }
  }, []);

  // Update existing post
  const handleUpdatePost = useCallback(async (id, formData) => {
    try {
      console.log('🔍 [useBlogPostActions] handleUpdatePost called with ID:', id);
      console.log('🔍 [useBlogPostActions] FormData received:', formData);
      
      // Log FormData contents
      console.log('🔍 [useBlogPostActions] FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`  - ${key}:`, value);
      }
      
      const updatedPost = await updateBlogPost(id, formData);
      console.log('✅ [useBlogPostActions] updateBlogPost successful, response:', updatedPost);
      
      setPosts(prevPosts => {
        const newPosts = prevPosts.map(post => 
          post.id === id ? updatedPost : post
      );
        console.log('🔍 [useBlogPostActions] Posts updated, new count:', newPosts.length);
        return newPosts;
      });
      
      return updatedPost;
    } catch (err) {
      console.error('❌ [useBlogPostActions] Error updating post:', err);
      setError('Error updating post');
      throw err;
    }
  }, []);

  // Delete single post
  const handleDeletePost = useCallback(async (id) => {
    try {
      await deleteBlogPost(id);
      setPosts(prevPosts => prevPosts.filter(post => post.id !== id));
      setSelectedPosts(prev => prev.filter(postId => postId !== id));
    } catch (err) {
      setError('Error deleting post');
      throw err;
    }
  }, []);

  // Bulk delete posts
  const handleBulkDeletePosts = useCallback(async () => {
    if (selectedPosts.length === 0) return;
    
    try {
      await bulkDeleteBlogPosts(selectedPosts);
      setPosts(prevPosts => 
        prevPosts.filter(post => !selectedPosts.includes(post.id))
      );
      setSelectedPosts([]);
    } catch (err) {
      setError('Error bulk deleting posts');
      throw err;
    }
  }, [selectedPosts]);

  // Selection handlers
  const handleSelectPost = useCallback((postId, checked) => {
    if (checked) {
      setSelectedPosts(prev => [...prev, postId]);
    } else {
      setSelectedPosts(prev => prev.filter(id => id !== postId));
    }
  }, []);

  const handleSelectAllPosts = useCallback((checked) => {
    if (checked) {
      setSelectedPosts(filteredPosts.map(post => post.id));
    } else {
      setSelectedPosts([]);
    }
  }, [filteredPosts]);

  // Filter handlers
  const handleFilterChange = useCallback((filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setSelectedPosts([]); // Clear selection when filters change
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      category: '',
      status: '',
    });
    setSelectedPosts([]);
  }, []);

  return {
    // Data
    posts: filteredPosts,
    allPosts: posts, // All posts for export functionality
    categories,
    loading,
    error,
    selectedPosts,
    filters,
    
    // Actions
    fetchData,
    handleAddPost,
    handleUpdatePost,
    handleDeletePost,
    handleBulkDeletePosts,
    
    // Selection
    handleSelectPost,
    handleSelectAllPosts,
    
    // Filters
    handleFilterChange,
    clearFilters,
  };
}; 